import React, { useEffect, useState } from 'react'
import { FaCcVisa, FaCcMastercard, FaCcAmex } from 'react-icons/fa'
import { FiEdit3, FiTrash2 } from 'react-icons/fi'
import { HiOutlineCreditCard } from 'react-icons/hi'
import { MdOutlineAddCard } from 'react-icons/md'
import { WarningBanners } from '../resumeStep/WarningBanners'
import CardAddForm, { CardFormData, toCreateCardPayload } from './components/CardAddForm'
import CaptureCvvModal from './components/CaptureCvvModal'
import { paymentService } from '../../../../../services/paymentService'
import api from '../../../../../services/api'
import { listSavedCards, deleteCard, createCard, findCreatedCardExternalId } from '../../../../../services/cards'
import { isProgramOrder, PROGRAM_INSTALLMENTS } from 'pages/WeightLossProgram/constants'
import { usePrepareCart } from '../../../../../hooks/usePrepareCart'
import { useTemporaryCvv } from '../../../../../hooks/useTemporaryCvv'
import { toast } from 'react-toastify'

interface CreditPaymentProps {
  selectedMethod: string
  cartItems: any[]
  shippingAddress?: any
}

const brandIcon = (brand?: string) => {
  const b = (brand || '').toLowerCase()
  if (b.includes('visa')) return <FaCcVisa className="text-2xl text-blue-600" />
  if (b.includes('master')) return <FaCcMastercard className="text-2xl text-red-500" />
  if (b.includes('amex')) return <FaCcAmex className="text-2xl text-blue-800" />
  return <HiOutlineCreditCard className="text-2xl text-gray-500" />
}

export const CreditPayment: React.FC<CreditPaymentProps> = ({
  selectedMethod: _selectedMethod,
  cartItems: _cartItems,
  shippingAddress
}) => {
  const [selectedCardExternalId, setSelectedCardExternalId] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [showCvv, setShowCvv] = useState(false)
  const { lastOrderCreated, recurrencyTermsAccepted } = usePrepareCart()
  // Compra Recorrente (007): pedido com plano paga à vista e leva o aceite do termo.
  const recurrencySummary = lastOrderCreated?.recurrencySummary ?? null
  const hasRecurrency = Boolean(recurrencySummary)
  const [cards, setCards] = useState<any[]>([])
  const [loadingCards, setLoadingCards] = useState(false)
  const [installments, setInstallments] = useState<
    Array<{ id: number; installment: string; amount: number; fees: string; total: number }>
  >([])
  const [loadingInstallments, setLoadingInstallments] = useState(false)
  const [selectedInstallmentId, setSelectedInstallmentId] = useState<number | null>(null)
  const { temporaryCvv, setTemporaryCvv, clearCvv } = useTemporaryCvv()
  const persistCreditSelection = (partial: Partial<{ installments: number; feesFactor: number }>) => {
    try {
      const raw = localStorage.getItem('@CANNECT:CREDIT_SELECTION')
      const prev = raw ? JSON.parse(raw) : {}
      const next = { ...prev, ...partial }
      localStorage.setItem('@CANNECT:CREDIT_SELECTION', JSON.stringify(next))
    } catch {
      /* ignore */
    }
  }

  const reloadCards = async () => {
    try {
      setLoadingCards(true)
      const fetched = await listSavedCards()
      const mapped = fetched.map((c) => {
        const externalId = c.external_id || String(c.id)
        return {
          id: c.id,
          external_id: externalId,
          type: c.brand || c.type || 'Cartão',
          last4: c.last4 || c.last_digits || '0000',
          holder: c.holder_name || c.holder || ''
        }
      })
      setCards(mapped)
      const stored = localStorage.getItem('@CANNECT:SELECTED_CARD_EXTERNAL_ID')
      if (stored) {
        setSelectedCardExternalId(stored)
      } else if (mapped[0]?.external_id) {
        setSelectedCardExternalId(mapped[0].external_id)
        localStorage.setItem('@CANNECT:SELECTED_CARD_EXTERNAL_ID', String(mapped[0].external_id))
      }
      if (mapped.length === 0) setShowForm(true)
      return fetched
    } catch (e) {
      setCards([])
      setShowForm(true)
      return []
    } finally {
      setLoadingCards(false)
    }
  }

  useEffect(() => {
    reloadCards()
  }, [])

  useEffect(() => {
    const fetchInstallments = async () => {
      if (!lastOrderCreated?.id) return
      try {
        setLoadingInstallments(true)
        const { data } = await api.get(`/payments/installments/${lastOrderCreated.id}`)
        const list = Array.isArray(data?.installments) ? data.installments : []
        const allowed = isProgramOrder(lastOrderCreated)
          ? list.filter((item) => Number(item.id) <= PROGRAM_INSTALLMENTS)
          : list
        setInstallments(allowed)
        // Limpa a seleção anterior para forçar o usuário a selecionar novamente
        localStorage.removeItem('@CANNECT:CREDIT_SELECTION')
        // Compra Recorrente (007): só à vista, sem escolha de parcelas.
        if (hasRecurrency) {
          setSelectedInstallmentId(1)
          persistCreditSelection({ installments: 1, feesFactor: 1 })
        }
      } catch {
        setInstallments([])
      } finally {
        setLoadingInstallments(false)
      }
    }
    fetchInstallments()
  }, [lastOrderCreated?.id, hasRecurrency])

  const handleAddCard = async (data: CardFormData) => {
    try {
      const created = await createCard(toCreateCardPayload(data))
      const fetched = await reloadCards()
      const cardExternalId = findCreatedCardExternalId(created, fetched, data.number)

      if (cardExternalId && data.cvv) {
        setTemporaryCvv((prev) => ({
          ...prev,
          [cardExternalId]: data.cvv
        }))

        setSelectedCardExternalId(cardExternalId)
        try {
          localStorage.setItem('@CANNECT:SELECTED_CARD_EXTERNAL_ID', String(cardExternalId))
        } catch {
          // ignore
        }
      }

      setShowForm(false)

      window.dispatchEvent(new CustomEvent('cardAdded'))
    } catch (e) {
      toast.error('Não foi possível adicionar o cartão')
    }
  }

  const handlePayWithSelected = async (cvv: string) => {
    if (!lastOrderCreated?.id || !lastOrderCreated?.amount) return
    if (!selectedCardExternalId) return
    if (hasRecurrency && !recurrencyTermsAccepted) {
      toast.error('Aceite o termo da Compra Recorrente')
      return
    }
    try {
      const baseTotal = Number(lastOrderCreated.amount)
      const selected = hasRecurrency ? undefined : installments.find((i) => i.id === selectedInstallmentId)
      const factor = selected && baseTotal > 0 ? Number(selected.total) / baseTotal : 1
      const totalWithFees = Math.max(0, baseTotal * factor)
      persistCreditSelection({ installments: selected?.id || 1, feesFactor: factor })
      await paymentService.generateSavedCardCharge(
        lastOrderCreated.id,
        selectedCardExternalId,
        cvv,
        totalWithFees,
        selected?.id || 1,
        hasRecurrency ? { recurrency: { termsVersion: recurrencySummary.termsVersion } } : {}
      )

      // Remove o CVV temporário após pagamento bem-sucedido
      if (temporaryCvv[selectedCardExternalId]) {
        clearCvv(selectedCardExternalId)
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || (e instanceof Error ? e.message : 'Erro ao processar pagamento')
      toast.error(msg)
    }
  }

  return (
    <div className="w-full space-y-6 rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
      {/* Header */}
      <div>
        <div className="mb-2 flex items-center gap-1">
          <HiOutlineCreditCard className="h-5 w-5 text-primary-700" />
          <h2 className="text-lg font-semibold text-primary-700">Pagamento cartão</h2>
        </div>
        <p className="text-sm text-neutral-800">Claro! Selecione um cartão para efetuar o pagamento.</p>
      </div>

      {/* Saved Cards */}
      <div className="rounded-lg bg-[#FAFAFA] p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center text-sm font-semibold text-gray-800">
            <HiOutlineCreditCard className="mr-1 h-5 w-5" />
            Cartões salvos
          </h3>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center border-0 bg-transparent text-xs font-medium text-primary-800 hover:underline">
            <MdOutlineAddCard className="mr-1 h-4 w-4 text-primary-800" />
            Adicionar
          </button>
        </div>

        <div className="space-y-3">
          {loadingCards && <div className="text-sm text-neutral-600">Carregando cartões...</div>}
          {!loadingCards && cards.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <HiOutlineCreditCard className="mb-3 h-12 w-12 text-gray-300" />
              <p className="mb-2 text-sm text-gray-500">Nenhum cartão cadastrado</p>
            </div>
          )}
          {cards.map((card) => (
            <div key={card.id} className="flex items-center justify-between">
              <label htmlFor={`card-${card.id}`} className="flex cursor-pointer items-center space-x-3">
                <input
                  id={`card-${card.id}`}
                  type="radio"
                  name="card"
                  checked={selectedCardExternalId === card.external_id}
                  onChange={() => {
                    setSelectedCardExternalId(card.external_id)
                    try {
                      localStorage.setItem('@CANNECT:SELECTED_CARD_EXTERNAL_ID', String(card.external_id))
                    } catch {
                      // ignore
                    }
                  }}
                  className="h-4 w-4 accent-primary-700"
                />
                <div className="flex items-center space-x-3">
                  {brandIcon(card.type)}
                  <div>
                    <p className="text-sm text-neutral-800">
                      {card.type} **** {card.last4}
                    </p>
                    <p className="text-xs text-neutral-500">{card.holder}</p>
                  </div>
                </div>
              </label>
              <div className="flex space-x-1">
                <button
                  className="border-0 bg-transparent text-neutral-500 hover:text-gray-800"
                  onClick={() => {
                    // Se tem CVV temporário, SEMPRE paga diretamente (nunca abre modal)
                    if (temporaryCvv[card.external_id]) {
                      // Seleciona o cartão se não estiver selecionado
                      if (selectedCardExternalId !== card.external_id) {
                        setSelectedCardExternalId(card.external_id)
                        localStorage.setItem('@CANNECT:SELECTED_CARD_EXTERNAL_ID', String(card.external_id))
                      }
                      handlePayWithSelected(temporaryCvv[card.external_id])
                    } else {
                      // Só abre modal se NÃO tem CVV temporário
                      setShowCvv(true)
                    }
                  }}>
                  <FiEdit3 className="h-4 w-4 text-neutral-500 hover:text-neutral-800" />
                </button>
                <button
                  className="border-0 bg-transparent text-error-200 hover:text-red-700"
                  onClick={async () => {
                    try {
                      await deleteCard(card.id)
                      setCards((prev) => prev.filter((c) => c.id !== card.id))
                      if (selectedCardExternalId === card.external_id) {
                        setSelectedCardExternalId(null)
                        localStorage.removeItem('@CANNECT:SELECTED_CARD_EXTERNAL_ID')
                      }
                    } catch (e) {
                      toast.error('Não foi possível excluir o cartão')
                    }
                  }}>
                  <FiTrash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showForm && (
        <CardAddForm onSubmit={handleAddCard} onCancel={() => setShowForm(false)} shippingAddress={shippingAddress} />
      )}
      {/* Parcelas (Compra Recorrente: só à vista, sem seleção) */}
      <div className={hasRecurrency ? 'hidden' : 'rounded-lg p-6'}>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="flex items-center text-sm font-semibold text-gray-800">Parcelas</h3>
        </div>
        {loadingInstallments && <div className="text-sm text-neutral-600">Carregando parcelas...</div>}
        {!loadingInstallments && installments.length > 0 && (
          <select
            className="h-11 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
            value={selectedInstallmentId || ''}
            onChange={(e) => {
              const { value } = e.target
              if (value === '') {
                setSelectedInstallmentId(null)
                return
              }
              const id = Number(value)
              setSelectedInstallmentId(id)
              const sel = installments.find((i) => i.id === id)
              const baseTotal = Number(lastOrderCreated.amount)
              const factor = sel && baseTotal > 0 ? Number(sel.total) / baseTotal : 1
              persistCreditSelection({ installments: id, feesFactor: factor })
            }}>
            <option value="">Selecione a quantidade de parcelas</option>
            {installments.map((it) => (
              <option
                key={it.id}
                value={
                  it.id
                }>{`${it.installment} de R$ ${Number(it.amount).toFixed(2)} ${it.fees === '(sem juros)' ? '- sem juros' : `- total R$ ${Number(it.total).toFixed(2)}`}`}</option>
            ))}
          </select>
        )}
      </div>
      <CaptureCvvModal
        isOpen={showCvv}
        onClose={() => setShowCvv(false)}
        onConfirm={(cvv) => handlePayWithSelected(cvv)}
        brand={cards.find((c) => c.external_id === selectedCardExternalId)?.type}
      />
      <WarningBanners
        containsProductWithLongerDeliveryTime={false}
        containProductsMatirz={false}
        containsProductWithHubDistributor={false}
      />
    </div>
  )
}
